import { useQuery } from "@tanstack/react-query"
import { useState } from "react"

import { api } from "@/api/cliente"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"

type Concepto = {
  codigo: string
  actividad: string
  pnr: string | null
  pnnr: string | null
  pjd: string | null
  pjnd: string | null
  nota: string | null
  alicuota: string | null
  sustraendo: string | null
}

type Catalogo = {
  tipo_persona: string | null
  nombre_tipo: string | null
  valor_ut: string
  conceptos: Concepto[]
}

/** «3 %» sin ceros de relleno. */
const pct = (v: string) => `${Number(v).toLocaleString("es-VE")} %`

/**
 * El concepto de la retención de ISLR según el Decreto 1.808 (22/10, `services/islr_1808.py`).
 *
 * Elegirlo PROPONE la alícuota y el sustraendo para el tipo de persona del proveedor —lo
 * resuelve el servidor por su RIF—; los dos campos siguen tecleables. Si el concepto no
 * aplica a ese tipo, o el decreto pide la tarifa N° 2, se dice y no se propone nada.
 */
export function ConceptoIslr({
  proveedorId,
  onProponer,
}: {
  proveedorId: number | null
  onProponer: (alicuota: string, sustraendo: string) => void
}) {
  const [elegido, setElegido] = useState<number | null>(null)
  const catalogo = useQuery({
    queryKey: ["islr-conceptos", proveedorId],
    queryFn: () =>
      api.get<Catalogo>(
        `/api/v1/islr/conceptos${proveedorId !== null ? `?proveedor_id=${proveedorId}` : ""}`,
      ),
  })
  const c = catalogo.data
  const concepto = elegido === null ? null : (c?.conceptos[elegido] ?? null)

  return (
    <div className="space-y-1">
      <label htmlFor="islr-concepto" className="block text-xs font-medium uppercase">
        Concepto (Decreto 1.808, art. 9)
      </label>
      <SelectorBuscable
        id="islr-concepto"
        opciones={(c?.conceptos ?? []).map((x, i) => ({
          valor: i,
          etiqueta: `${x.codigo} · ${x.actividad}`,
          detalle: [
            x.pnr && `PNR ${pct(x.pnr)}`,
            x.pnnr && `PNNR ${pct(x.pnnr)}`,
            x.pjd && `PJD ${pct(x.pjd)}`,
            x.pjnd && `PJND ${pct(x.pjnd)}`,
          ]
            .filter(Boolean)
            .join(" · "),
        }))}
        valor={elegido}
        onCambiar={(i) => {
          setElegido(i)
          const x = i === null ? null : c?.conceptos[i]
          if (x?.alicuota != null) onProponer(x.alicuota, x.sustraendo ?? "0")
        }}
        vacio="Sin concepto: alícuota a mano"
        placeholder="Honorarios, fletes, arrendamiento…"
      />
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        {c?.nombre_tipo
          ? `El proveedor es ${c.nombre_tipo.toLowerCase()} (por su RIF).`
          : "No se sabe el tipo de persona del proveedor: revisa su RIF."}
        {concepto &&
          (concepto.alicuota === null
            ? " Este concepto no tiene un porcentaje fijo para ese tipo de persona: teclea la alícuota."
            : ` Se propone ${pct(concepto.alicuota)}${
                concepto.sustraendo && Number(concepto.sustraendo) > 0
                  ? ` y un sustraendo de ${concepto.sustraendo} (UT ${c?.valor_ut} × % × 83,3334)`
                  : ""
              }.`)}
        {concepto?.nota && ` ${concepto.nota}`}
      </p>
    </div>
  )
}
